import YachtManufacturersService from '../../services/api/YachtManufacturers'
import YachtTypesService from '../../services/api/YachtTypes'
import YachtEnginesService from '../../services/api/YachtEngines'
import CountriesService from '../../services/api/Countries'
import ContractorsService from '../../services/api/Contractors'
import YachtModelsService from '../../services/api/YachtModels'
import YachtEquipmentPackCategoriesService from '../../services/api/YachtEquipmentPackCategories'
import CurrencyExchangeRate from '@/models/CurrencyExchangeRate'
import ability from '@/plugins/ability'

// initial state
const state = {
  yachtManufacturers: {
    data: [],
    loading: false
  },
  yachtTypes: {
    data: [],
    loading: false
  },
  yachtEngines: {
    data: [],
    loading: false
  },
  countries: {
    data: [],
    loading: false
  },
  equipmentPackCategories: {
    data: [],
    loading: false
  },
  contractors: {
    data: [],
    loading: false
  },
  currencyExchangeRates: {
    data: [],
    loading: false
  },
  yachtModelSpecs: {
    data: [],
    loading: false
  }
}

// getters
const getters = {
  contractors(state) {
    return state.contractors
  },
  countries(state) {
    return state.countries
  },
  charteringCountries(state) {
    return {
      loading: state.countries.loading,
      data: state.countries.data.filter(c => !!c.chartering)
    }
  },
  charteringCountriesAllOrganisations(state) {
    return {
      loading: state.countries.loading,
      data: state.countries.data.filter(c => !!c.chartering_any)
    }
  },
  yachtTypes(state) {
    return state.yachtTypes
  },
  yachtManufacturers(state) {
    return state.yachtManufacturers
  },
  currencyExchangeRates(state) {
    return state.currencyExchangeRates
  },
  yachtModelSpecs(state) {
    return state.yachtModelSpecs
  },
  equipmentPackCategories(state) {
    return state.equipmentPackCategories
  },
  equipmentPackCategoryCount(state) {
    return state.equipmentPackCategories.data.length
  }
}

// actions
const actions = {
  getBasicData({ dispatch }) {
    dispatch('getYachtManufacturers')
    dispatch('getYachtTypes')
    dispatch('getYachtEngines')
    dispatch('getCountries')
    dispatch('getEquipmentPackCategories')
    dispatch('getContractors')
    dispatch('fetchCurrencyExchangeRates')
    dispatch('fetchYachtModelSpecs')
  },
  async getYachtManufacturers({ commit }) {
    if (!ability.can('view', 'YachtManufacturer')) return

    commit('YACHT_MANUFACTURERS_LOADING', true)

    let manufacturers = await YachtManufacturersService.fetch({ order_by: 'name' })

    commit('YACHT_MANUFACTURERS_LOADING', false)
    commit('SET_YACHT_MANUFACTURERS', {
      data: manufacturers
    })
  },
  async getYachtTypes({ commit }) {
    if (!ability.can('view', 'YachtType')) return

    commit('YACHT_TYPES_LOADING', true)

    let types = await YachtTypesService.fetch({ order_by: 'label' })

    commit('YACHT_TYPES_LOADING', false)
    commit('SET_YACHT_TYPES', {
      data: types
    })
  },
  async getYachtEngines({ commit }) {
    if (!ability.can('view', 'YachtEngine')) return

    commit('YACHT_ENGINES_LOADING', true)

    let engines = await YachtEnginesService.fetch()

    commit('YACHT_ENGINES_LOADING', false)
    commit('SET_YACHT_ENGINES', {
      data: engines
    })
  },
  async getCountries({ commit }) {
    if (!ability.can('view', 'Country')) return

    commit('COUNTRIES_LOADING', true)

    let countries = await CountriesService.fetch()

    commit('COUNTRIES_LOADING', false)
    commit('SET_COUNTRIES', {
      data: countries
    })
  },
  async getEquipmentPackCategories({ commit }) {
    if (!ability.can('view', 'YachtEquipmentPackCategory')) return

    commit('EQUIPMENT_PACK_CATEGORIES_LOADING', true)

    let categories = await YachtEquipmentPackCategoriesService.fetch({ order_by: 'order' })

    commit('EQUIPMENT_PACK_CATEGORIES_LOADING', false)
    commit('SET_EQUIPMENT_PACK_CATEGORIES', {
      data: categories
    })
  },
  async getContractors({ commit }) {
    if (!ability.can('view', 'Contractor')) return

    commit('CONTRACTORS_LOADING', true)

    let contractors = await ContractorsService.fetch()

    commit('CONTRACTORS_LOADING', false)
    commit('SET_CONTRACTORS', {
      data: contractors
    })
  },
  async fetchCurrencyExchangeRates({ commit }) {
    commit('CURRENCY_EXCHANGE_RATES_LOADING', true)

    let currencyExchangeRates = await CurrencyExchangeRate.api.fetchYearly()

    commit('CURRENCY_EXCHANGE_RATES_LOADING', false)
    commit('SET_CURRENCY_EXCHANGE_RATES', {
      data: currencyExchangeRates
    })
  },
  async fetchYachtModelSpecs({ commit }) {
    if (!ability.can('view', 'YachtModel')) return

    commit('YACHT_MODEL_SPECS_LOADING', true)

    let yachtModelSpecs = await YachtModelsService.fetchSpecs()

    commit('YACHT_MODEL_SPECS_LOADING', false)
    commit('SET_YACHT_MODEL_SPECS', {
      data: yachtModelSpecs
    })
  }
}

// mutations
const mutations = {
  YACHT_MANUFACTURERS_LOADING(state, value) {
    state.yachtManufacturers.loading = value
  },
  SET_YACHT_MANUFACTURERS(state, { data }) {
    state.yachtManufacturers.data = data
  },
  YACHT_TYPES_LOADING(state, value) {
    state.yachtTypes.loading = value
  },
  SET_YACHT_TYPES(state, { data }) {
    state.yachtTypes.data = data
  },
  YACHT_ENGINES_LOADING(state, value) {
    state.yachtEngines.loading = value
  },
  SET_YACHT_ENGINES(state, { data }) {
    state.yachtEngines.data = data
  },
  COUNTRIES_LOADING(state, value) {
    state.yachtEngines.loading = value
  },
  SET_COUNTRIES(state, { data }) {
    state.countries.data = data
  },
  CONTRACTORS_LOADING(state, value) {
    state.contractors.loading = value
  },
  SET_CONTRACTORS(state, { data }) {
    state.contractors.data = data
  },
  EQUIPMENT_PACK_CATEGORIES_LOADING(state, value) {
    state.equipmentPackCategories.loading = value
  },
  SET_EQUIPMENT_PACK_CATEGORIES(state, { data }) {
    state.equipmentPackCategories.data = data
  },
  CURRENCY_EXCHANGE_RATES_LOADING(state, value) {
    state.currencyExchangeRates.loading = value
  },
  SET_CURRENCY_EXCHANGE_RATES(state, { data }) {
    state.currencyExchangeRates.data = data
  },
  YACHT_MODEL_SPECS_LOADING(state, value) {
    state.yachtModelSpecs.loading = value
  },
  SET_YACHT_MODEL_SPECS(state, { data }) {
    state.yachtModelSpecs.data = data
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
